import { Divider } from "@msuite/ui/divider";
import { Textarea, TextInput } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { WeekdayPicker } from "@msuite/ui/weekday-picker";
import type { Tables } from "@msuite/utils/types";
import { useFormContext, useWatch } from "react-hook-form";
import { ContactPicker } from "@/components/contacts";
import { ProjectMemberPicker } from "@/components/pickers/project-member-picker";
import { WorkspacePicker } from "@/components/pickers/workspace-picker";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { AddressForm } from "../address-form";
import { RelatedProject } from "./related-project";
import type { ProjectMemberController } from "./use-project-member-controller";

/** Props Interface */
interface ProjectFormProps {
	projectMemberController: ProjectMemberController;
}

export function ProjectForm({ projectMemberController }: ProjectFormProps) {
	/** Context */
	const methods = useFormContext<Tables<"projects">>();
	const [streetName, houseNumber] = useWatch({
		control: methods.control,
		name: ["streetName", "houseNumber"],
	});

	/** Hooks */
	const mentionOptions = useMentionOptions();
	const { t } = useTranslation();

	/** Render */
	return (
		<div className="flex flex-col gap-6">
			<WorkspacePicker
				methods={methods}
				control={methods.control}
				name="workspaceId"
				selectDefault
			/>
			<TextInput
				autoFocus
				label={t("projects.fields.project_number")}
				placeholder="2025-1235"
				control={methods.control}
				name="number"
				isRequired={false}
				data-cy="project-number-input"
			/>
			<TextInput
				label={t("projects.fields.project_name")}
				placeholder={`${streetName ?? ""} ${houseNumber ?? ""}`.trim() || "Schillerstraße 3"}
				control={methods.control}
				name="name"
				isRequired={false}
				data-cy="project-name-input"
			/>
			<Divider variant="modal" />
			<ContactPicker
				control={methods.control}
				name="clientId"
				label={t("projects.fields.client")}
			/>
			<Divider variant="modal" />
			<ProjectMemberPicker
				projectMemberController={projectMemberController}
				isRequired
				label={t("projects.fields.manager")}
				name="manager"
				data-cy="project-member-picker"
			/>
			<ProjectMemberPicker
				projectMemberController={projectMemberController}
				isRequired
				label={t("projects.fields.deputy_managers")}
				name="deputyManagers"
				data-cy="project-member-picker-deputy-managers"
			/>
			<Divider variant="modal" />
			{/* Which days work may happen on this site. It is what a ticket's estimate is counted in,
			    so a site that works Saturdays draws six columns a week and one beside it draws five. */}
			<WeekdayPicker
				control={methods.control}
				name="weekdays"
			/>
			<Divider variant="modal" />
			<AddressForm />
			<Divider variant="modal" />
			<Textarea
				placeholder="Bitte die Haupteinfahrt nutzen"
				label="Hinweis"
				control={methods.control}
				name="note"
				isRequired={false}
				disableFormatting
				{...mentionOptions}
				data-cy="project-note-input"
			/>
			<RelatedProject />
		</div>
	);
}
